import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
// import reportWebVitals from './reportWebVitals';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You can also log the error to an error reporting service here
    // logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '100%'
          }}>
            <h1 style={{ marginBottom: '1rem', fontSize: '2rem' }}>
              ⚠️ Something went wrong
            </h1>
            <p style={{ marginBottom: '1.5rem', fontSize: '1.1rem', opacity: 0.9 }}>
              The Remote Service Manager encountered an unexpected error.
            </p>
            
            <div style={{ marginBottom: '2rem' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  marginRight: '1rem',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              >
                🔄 Reload Page
              </button>
              
              <button
                onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              >
                🔄 Try Again
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                background: 'rgba(0,0,0,0.3)',
                padding: '1rem',
                borderRadius: '4px',
                textAlign: 'left',
                marginTop: '1rem'
              }}>
                <summary style={{ cursor: 'pointer', marginBottom: '0.5rem' }}>
                  🐛 Error Details (Development)
                </summary>
                <pre style={{
                  fontSize: '0.8rem',
                  overflow: 'auto',
                  background: 'rgba(0,0,0,0.2)',
                  padding: '0.5rem',
                  borderRadius: '2px',
                  marginBottom: '0.5rem'
                }}>
                  {this.state.error && this.state.error.toString()}
                </pre>
                <pre style={{
                  fontSize: '0.7rem',
                  overflow: 'auto',
                  background: 'rgba(0,0,0,0.2)',
                  padding: '0.5rem',
                  borderRadius: '2px'
                }}>
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            
            <p style={{ 
              fontSize: '0.9rem', 
              opacity: 0.7, 
              marginTop: '1rem',
              fontStyle: 'italic'
            }}>
              If this problem persists, please check the browser console for more details.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Create root element
const root = ReactDOM.createRoot(document.getElementById('root'));

// Render app with error boundary
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Performance measuring function
function sendToAnalytics(metric) {
  // In a real app, you would send these metrics to your analytics service
  console.log('Web Vital:', metric);
  
  // Example: Send to Google Analytics 4
  // gtag('event', metric.name, {
  //   event_category: 'Web Vitals',
  //   value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
  //   event_label: metric.id,
  //   non_interaction: true,
  // });
}

// Measure and report web vitals
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals(sendToAnalytics);

// Service Worker registration
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
        
        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content is available, show update notification
              if (window.confirm('New version available! Reload to update?')) {
                window.location.reload();
              }
            }
          });
        });
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}

// Handle app updates
let deferredPrompt;

window.addEventListener('beforeinstallprompt', (e) => {
  // Prevent Chrome 67 and earlier from automatically showing the prompt
  e.preventDefault();
  // Stash the event so it can be triggered later
  deferredPrompt = e;
  
  // Show install banner/button (you can implement this in your app)
  console.log('App can be installed');
});

window.addEventListener('appinstalled', (evt) => {
  console.log('App was installed');
});

// Global error handling
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
});

// Development helpers
if (process.env.NODE_ENV === 'development') {
  // Log React version
  console.log('React version:', React.version);
  
  // Enable React DevTools profiler
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__.settings = {
      ...window.__REACT_DEVTOOLS_GLOBAL_HOOK__.settings,
      profilerStartTime: Date.now()
    };
  }
  
  // Hot module replacement for development
  if (module.hot) {
    module.hot.accept('./App', () => {
      const NextApp = require('./App').default;
      root.render(
        <React.StrictMode>
          <ErrorBoundary>
            <NextApp />
          </ErrorBoundary>
        </React.StrictMode>
      );
    });
  }
}

// Keyboard shortcuts
document.addEventListener('keydown', (event) => {
  // Alt + R to reload in development
  if (process.env.NODE_ENV === 'development' && event.altKey && event.key === 'r') {
    event.preventDefault();
    window.location.reload();
  }
  
  // Ctrl/Cmd + K to focus search (if implemented)
  if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
    event.preventDefault();
    // Dispatch custom event that App component can listen to
    document.dispatchEvent(new CustomEvent('focus-search'));
  }
});

// App lifecycle events
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    console.log('App became visible');
    // Dispatch event for app components to handle
    document.dispatchEvent(new CustomEvent('app-focus'));
  } else {
    console.log('App became hidden');
    document.dispatchEvent(new CustomEvent('app-blur'));
  }
});

// Memory usage monitoring (development only)
if (process.env.NODE_ENV === 'development' && 'memory' in performance) {
  setInterval(() => {
    const memory = performance.memory;
    if (memory.usedJSHeapSize > 50 * 1024 * 1024) { // 50MB threshold
      console.warn('High memory usage detected:', {
        used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + 'MB',
        total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + 'MB',
        limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + 'MB'
      });
    }
  }, 30000); // Check every 30 seconds
}

// Export for testing
export { ErrorBoundary };